import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Gift, Lock, Loader2, Timer, X, Store, Share2, Camera, CameraOff, CheckCircle2, QrCode, RefreshCw, AlertTriangle, Wallet, Plus, CreditCard, Sparkles, ShieldCheck } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import * as Sentry from '@sentry/react';
import { walletService, type WalletBalance, type Reward, type PurseBalance } from '../services/walletService';
import { brandService, type BrandInfo } from '../services/brandService';
import { useAuth } from '../contexts/AuthContext';
import { formatEuro } from '../utils/currency';
import { resolveTopUpBonus } from '../utils/topUpBonus';
import { savePendingOnlineTopUp, redirectToRedsys } from '../utils/onlineTopUp';

// Fallback recharge presets (€) when a Brand hasn't configured its own quick-add amounts,
// mirroring the business-dashboard defaults so both sides offer the same ladder.
const DEFAULT_TOPUP_AMOUNTS = [5, 10, 20, 50];

const API_URL = import.meta.env.VITE_API_URL ?? '';

function getLogoUrl(logoUrl: string): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://') || logoUrl.startsWith('blob:')) {
        return logoUrl;
    }
    const base = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    const path = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`;
    return `${base}${path}`;
}

function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// Mirrors the backend PointsEarnEngine (floor(amount * 10)) so the pre-charge
// screen can preview the points a Spend will earn. Keep in sync with the server.
const POINTS_PER_EURO = 10;

function previewPointsEarned(amount: number): number {
    return Math.floor(amount * POINTS_PER_EURO);
}

type ScanMode = 'points' | 'topup' | 'spend';

interface StoredValueQrPayload {
    kind: 'topup' | 'spend';
    code: string;
    amount: number;
}

/**
 * A top-up / charge QR carries a structured payload {kind, code, amount} minted by
 * the POS, so the User can review the amount before confirming. The charge is still
 * authorized server-side by `code` alone; `amount` is advisory display data. Returns
 * null for a plain points-earn code (or any malformed payload).
 */
function parseStoredValueQr(decodedText: string): StoredValueQrPayload | null {
    try {
        const parsed = JSON.parse(decodedText);
        if (
            parsed &&
            typeof parsed === 'object' &&
            (parsed.kind === 'topup' || parsed.kind === 'spend') &&
            typeof parsed.code === 'string' &&
            typeof parsed.amount === 'number'
        ) {
            return { kind: parsed.kind, code: parsed.code, amount: parsed.amount };
        }
    } catch {
        // Not JSON — a plain points-earn code, handled by the points flow.
    }
    return null;
}

export default function WalletDetailPage() {
    const { brandId } = useParams<{ brandId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    // Brand info can come from navigation state (from Wallet page) with balance, but brand info itself is fetched
    const navState = location.state as WalletBalance | null;

    const { user: userProfile } = useAuth();
    const [brandInfo, setBrandInfo] = useState<BrandInfo | null>(null);
    const [balance, setBalance] = useState(navState?.balance ?? 0);
    const [purse, setPurse] = useState<PurseBalance | null>(null);
    const [purseError, setPurseError] = useState<string | null>(null);
    const [rewards, setRewards] = useState<Reward[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // OTP redemption state
    const [redeemCode, setRedeemCode] = useState<string | null>(null);
    const [redeemReward, setRedeemReward] = useState<Reward | null>(null);
    const [timeLeft, setTimeLeft] = useState(0);
    const [isGenerating, setIsGenerating] = useState<string | null>(null);
    const [showRedeem, setShowRedeem] = useState(false);
    const [showToast, setShowToast] = useState(false);

    // Loyalty OTP state (added in Step 47)
    const [loyaltyOtp, setLoyaltyOtp] = useState<string | null>(null);
    const [loyaltyTimeLeft, setLoyaltyTimeLeft] = useState(0);
    const [isGeneratingLoyalty, setIsGeneratingLoyalty] = useState(false);
    const [showLoyaltyCode, setShowLoyaltyCode] = useState(false);

    // QR scanner state
    const [showScanner, setShowScanner] = useState(false);
    const [scanSuccess, setScanSuccess] = useState<{ pointsEarned: number; newBalance: number } | null>(null);
    const [cameraError, setCameraError] = useState<string | null>(null);

    // StoredValue (saldo en €) scan flows: recarga (top-up) and pago (spend).
    // `scanMode` tells handleScanSuccess how to route the decoded QR.
    const [scanMode, setScanMode] = useState<ScanMode>('points');
    const [pendingSpend, setPendingSpend] = useState<{ code: string; amount: number } | null>(null);
    const [isConfirmingSpend, setIsConfirmingSpend] = useState(false);
    const [topUpSuccess, setTopUpSuccess] = useState<{ amount: number } | null>(null);
    const [spendSuccess, setSpendSuccess] = useState<{ amount: number; pointsEarned: number } | null>(null);
    // Non-fatal operation error (recarga/pago) shown inline — unlike `error`, it
    // does not replace the whole page (e.g. saldo insuficiente must stay blocking).
    const [opError, setOpError] = useState<string | null>(null);

    // Online recharge (Redsys redirect) flow. The sheet lets the User pick an amount and
    // preview the bonus before being sent to the Brand's hosted TPV Virtual page to pay.
    const [showOnlineTopUp, setShowOnlineTopUp] = useState(false);
    const [onlineAmount, setOnlineAmount] = useState<number | null>(null);
    const [isCreatingPayment, setIsCreatingPayment] = useState(false);
    const [onlineTopUpError, setOnlineTopUpError] = useState<string | null>(null);

    // Scanner refs (Bug #1 & #4 fixes)
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const handleScanSuccessRef = useRef<(decodedText: string) => Promise<void>>(async () => { });

    // Fetch balance + rewards
    useEffect(() => {
        if (!brandId) return;

        const fetchData = async () => {
            try {
                const [info, balances, rewardsData] = await Promise.all([
                    brandService.getBrandInfo(brandId),
                    !navState ? walletService.getBalance() : Promise.resolve(null),
                    walletService.getBrandRewards(brandId),
                ]);

                setBrandInfo(info);

                if (!navState && balances) {
                    const found = balances.find((b) => b.brandId === brandId);
                    setBalance(found ? found.balance : 0);
                }

                setRewards(rewardsData);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [brandId, navState]);

    // Fetch StoredValue ("saldo en €") once we know the brand has the module.
    useEffect(() => {
        if (!brandId || !brandInfo?.hasStoredValue) {
            setPurse(null);
            setPurseError(null);
            return;
        }

        let cancelled = false;
        setPurseError(null);
        walletService.getPurseBalance(brandId)
            .then((result) => {
                if (!cancelled) setPurse(result);
            })
            .catch((err: any) => {
                if (!cancelled) setPurseError(err?.message ?? 'Error al obtener el saldo en €.');
            });

        return () => {
            cancelled = true;
        };
    }, [brandId, brandInfo?.hasStoredValue]);

    // Countdown timer for redeem codes
    useEffect(() => {
        if (timeLeft <= 0) {
            if (redeemCode) {
                setRedeemCode(null);
                setRedeemReward(null);
                setShowRedeem(false);
            }
            return;
        }
        const interval = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [timeLeft, redeemCode]);

    const handleRedeem = useCallback(async (reward: Reward) => {
        // If there's already a valid code, just re-show it
        if (redeemCode && redeemReward?.id === reward.id && timeLeft > 0) {
            setShowRedeem(true);
            return;
        }

        setIsGenerating(reward.id);
        try {
            const response = await walletService.generateOtp(reward.id);
            setRedeemCode(response.otpCode);
            setRedeemReward(reward);
            setTimeLeft(response.expiresInSeconds);
            setShowRedeem(true);
        } catch (err) {
            console.error('Error generating redeem OTP:', err);
        } finally {
            setIsGenerating(null);
        }
    }, [redeemCode, redeemReward, timeLeft]);

    const handleCancelRedeem = useCallback(() => {
        setShowRedeem(false);
    }, []);

    // Loyalty OTP Handlers (added in Step 47)
    const handleGenerateLoyaltyCode = useCallback(async () => {
        if (loyaltyOtp && loyaltyTimeLeft > 0) {
            setShowLoyaltyCode(true);
            return;
        }

        setIsGeneratingLoyalty(true);
        try {
            const response = await walletService.generateOtp();
            setLoyaltyOtp(response.otpCode);
            setLoyaltyTimeLeft(response.expiresInSeconds);
            setShowLoyaltyCode(true);
        } catch (err) {
            console.error('Error generating loyalty OTP:', err);
        } finally {
            setIsGeneratingLoyalty(false);
        }
    }, [loyaltyOtp, loyaltyTimeLeft]);

    const handleCancelLoyaltyCode = useCallback(() => {
        setShowLoyaltyCode(false);
    }, []);

    // Loyalty OTP Timer
    useEffect(() => {
        if (loyaltyTimeLeft <= 0) {
            if (loyaltyOtp) {
                setLoyaltyOtp(null);
                setShowLoyaltyCode(false);
            }
            return;
        }
        const interval = setInterval(() => {
            setLoyaltyTimeLeft((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [loyaltyTimeLeft, loyaltyOtp]);

    const handleShare = async () => {
        if (!brandInfo || !userProfile?.personalReferralCode) return;

        const referralUrl = `${window.location.origin}/register?ref=${userProfile.personalReferralCode}&brandId=${brandInfo.id}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `¡Únete a ${brandInfo.name} en Zovy Rewards!`,
                    text: `¡Hola! Únete a ${brandInfo.name} con mi enlace y ganaremos puntos extra en tu primera compra.`,
                    url: referralUrl,
                });
            } catch (err) {
                console.error('Error sharing:', err);
                copyToClipboard(referralUrl);
            }
        } else {
            copyToClipboard(referralUrl);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
        });
    };

    const handleStartScan = (mode: ScanMode = 'points') => {
        setScanMode(mode);
        setCameraError(null);
        setOpError(null);
        setShowScanner(true);
        setScanSuccess(null);
        setTopUpSuccess(null);
        setSpendSuccess(null);
    };

    // stop camera BEFORE unmounting DOM. useCallback with no stale closure risk.
    const handleScanSuccess = useCallback(async (decodedText: string) => {
        // 1. First stop the scanner and release the camera
        if (scannerRef.current?.isScanning) {
            await scannerRef.current.stop().catch(() => { });
        }
        scannerRef.current = null;

        // 2. Then unmount the modal
        setShowScanner(false);

        // 3a. Points earn (existing flow): auto-submit the decoded code.
        if (scanMode === 'points') {
            const scanTimeoutId = setTimeout(() => {
                Sentry.captureMessage("Timeout silencioso en Procesamiento de QR (Claim Code)", "warning");
            }, 10000);

            try {
                const response = await walletService.scanQr(decodedText);
                clearTimeout(scanTimeoutId);
                setScanSuccess(response);
                setBalance(response.newBalance);
            } catch (err: any) {
                clearTimeout(scanTimeoutId);
                setError(err.message || 'Error al procesar el QR');
            }
            return;
        }

        // 3b. StoredValue flows carry a structured payload {kind, code, amount}.
        const payload = parseStoredValueQr(decodedText);
        if (!payload || payload.kind !== scanMode) {
            setOpError(
                scanMode === 'topup'
                    ? 'El código escaneado no es un QR de recarga válido.'
                    : 'El código escaneado no es un QR de pago válido.'
            );
            return;
        }

        // Top-up only adds money → no pre-confirmation, credit and show the banner.
        if (scanMode === 'topup') {
            try {
                const updated = await walletService.claimTopUp(payload.code);
                setPurse(updated);
                setTopUpSuccess({ amount: payload.amount });
            } catch (err: any) {
                setOpError(err.message || 'Error al procesar la recarga.');
            }
            return;
        }

        // Spend: DO NOT charge here. Route to the pre-charge confirmation screen;
        // the debit only happens when the User taps "Confirmar pago".
        setPendingSpend({ code: payload.code, amount: payload.amount });
    }, [scanMode]);

    const handleConfirmSpend = useCallback(async () => {
        if (!pendingSpend) return;

        setIsConfirmingSpend(true);
        setOpError(null);
        try {
            const result = await walletService.confirmSpend(pendingSpend.code);
            setPurse({
                realBalance: result.realBalance,
                bonusBalance: result.bonusBalance,
                totalBalance: result.totalBalance,
            });
            setBalance(result.newPointsBalance);
            setSpendSuccess({ amount: pendingSpend.amount, pointsEarned: result.pointsEarned });
            setPendingSpend(null);
        } catch (err: any) {
            // Includes the "saldo insuficiente" blocking message from the service.
            setOpError(err.message || 'Error al confirmar el pago.');
            setPendingSpend(null);
        } finally {
            setIsConfirmingSpend(false);
        }
    }, [pendingSpend]);

    const handleCancelSpend = useCallback(() => {
        setPendingSpend(null);
    }, []);

    // Online recharge presets: the Brand's own quick-add amounts, or the app defaults.
    const topUpAmounts = brandInfo?.quickAddAmounts?.length ? brandInfo.quickAddAmounts : DEFAULT_TOPUP_AMOUNTS;

    const handleStartOnlineTopUp = useCallback(() => {
        setOnlineTopUpError(null);
        setOpError(null);
        // Preselect the first preset so the bonus preview isn't empty on open.
        setOnlineAmount((prev) => prev ?? topUpAmounts[0] ?? null);
        setShowOnlineTopUp(true);
    }, [topUpAmounts]);

    const handleCloseOnlineTopUp = useCallback(() => {
        if (isCreatingPayment) return; // don't yank the sheet mid-redirect
        setShowOnlineTopUp(false);
    }, [isCreatingPayment]);

    // Create the signed payment, remember the in-flight recharge, then redirect the browser
    // to the Redsys hosted page. The saldo is credited by the S2S notification, so the return
    // route (/topup/ok) polls for it — nothing is credited here.
    const handleConfirmOnlineTopUp = useCallback(async () => {
        if (!brandId || onlineAmount == null || onlineAmount <= 0) return;

        setIsCreatingPayment(true);
        setOnlineTopUpError(null);
        try {
            const envelope = await walletService.createTopUpPayment(brandId, onlineAmount);
            if (!envelope.redsysUrl || !envelope.signatureVersion || !envelope.merchantParameters || !envelope.signature) {
                throw new Error('No se pudo iniciar la recarga online. Por favor, inténtalo de nuevo.');
            }

            // Capture a fresh baseline right before leaving: the return page detects the
            // credit as a rise above this figure. Reading it live (not the possibly-still-
            // loading `purse` state) avoids a stale 0 baseline flagging an uncredited return
            // as complete. Fall back to whatever we already know if the read fails.
            let baselineTotal = purse?.totalBalance ?? 0;
            try {
                const fresh = await walletService.getPurseBalance(brandId);
                baselineTotal = fresh.totalBalance;
            } catch {
                // Keep the fallback baseline; a live read isn't essential to redirect.
            }

            savePendingOnlineTopUp({
                brandId,
                amount: onlineAmount,
                order: envelope.order,
                baselineTotal,
                startedAt: Date.now(),
            });

            redirectToRedsys(envelope);
            // Navigation leaves the app; no further state updates expected.
        } catch (err: any) {
            setOnlineTopUpError(err.message || 'No se pudo iniciar la recarga online.');
            setIsCreatingPayment(false);
        }
    }, [brandId, onlineAmount, purse]);

    // keep ref in sync so the scanner callback always uses the latest version
    useEffect(() => {
        handleScanSuccessRef.current = handleScanSuccess;
    });

    // setTimeout for DOM readiness, no double stop, playsinline.
    useEffect(() => {
        if (!showScanner) return;

        let cancelled = false;

        const timeout = setTimeout(() => {
            if (cancelled) return;

            const scanner = new Html5Qrcode("reader");
            scannerRef.current = scanner;

            const initTimeoutId = setTimeout(() => {
                Sentry.captureMessage("Timeout silencioso en Inicialización de Cámara", "warning");
            }, 10000);

            scanner.start(
                { facingMode: "environment" },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 }
                },
                (decodedText) => {
                    // Use the ref to avoid stale closure
                    handleScanSuccessRef.current(decodedText);
                    // Do NOT call scanner.stop() here — handleScanSuccess manages it
                },
                (_errorMessage) => {
                    // Ignore per-frame scan errors (normal during scanning)
                }
            )
                .then(() => {
                    clearTimeout(initTimeoutId);
                    // ensure playsinline for iOS Safari
                    const video = document.querySelector('#reader video') as HTMLVideoElement | null;
                    if (video) {
                        video.setAttribute('playsinline', 'true');
                        video.setAttribute('webkit-playsinline', 'true');
                        video.muted = true;
                    }
                })
                .catch(err => {
                    if (cancelled) return;
                    clearTimeout(initTimeoutId);
                    console.error("Scanner start error:", err);
                    const msg = String(err?.message || err || '');
                    if (
                        msg.includes('NotAllowedError') ||
                        msg.includes('Permission') ||
                        msg.includes('denied') ||
                        msg.includes('permission')
                    ) {
                        setCameraError('denied');
                    } else {
                        setCameraError('general');
                    }
                });
        }, 100);

        return () => {
            cancelled = true;
            clearTimeout(timeout);
            const s = scannerRef.current;
            if (s?.isScanning) {
                s.stop().catch(() => { });
            }
            scannerRef.current = null;
        };
    }, [showScanner]);

    if (error) {
        return (
            <div className="flex flex-col">
                <div className="sticky top-0 z-10 flex items-center gap-3 bg-white/95 px-5 pb-3 pt-12 backdrop-blur-sm">
                    <button onClick={() => navigate(-1)} className="cursor-pointer rounded-full p-1 hover:bg-gray-100">
                        <ArrowLeft size={24} className="text-gray-700" />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900">Error</h1>
                </div>
                <div className="px-5 pt-6 text-center">
                    <p className="text-sm text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="sticky top-0 z-10 flex flex-col bg-white/95 px-5 pb-3 pt-12 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="cursor-pointer rounded-full p-1 transition-colors hover:bg-gray-100"
                    >
                        <ArrowLeft size={24} className="text-gray-700" />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900">{brandInfo?.name ?? 'Cargando...'}</h1>
                </div>
                <span className={`ml-10 text-xs font-medium text-gray-500 uppercase tracking-wider mt-1 ${brandInfo?.category ? 'block' : 'hidden'}`}>{brandInfo?.category || '\u00A0'}</span>
                <p className={`ml-10 mt-2 text-sm text-gray-600 leading-relaxed ${brandInfo?.description ? 'block' : 'hidden'}`}>{brandInfo?.description || '\u00A0'}</p>
            </div>

            <div className="flex flex-col gap-5 px-5 pb-6">
                {/* Redeem OTP Banner */}
                {showRedeem && redeemCode && redeemReward && (
                    <div className="relative rounded-3xl bg-green-600 p-5 text-white shadow-lg shadow-green-200">
                        <button
                            onClick={handleCancelRedeem}
                            className="absolute right-4 top-4 cursor-pointer rounded-full p-1 transition-colors hover:bg-white/20"
                        >
                            <X size={20} />
                        </button>

                        <p className="mb-1 text-center text-sm font-medium text-green-200">
                            Canjeando: {redeemReward.name}
                        </p>
                        <p className="mb-1 text-center text-xs text-green-200">
                            Muestra este código en caja
                        </p>

                        <p className="mb-4 text-center font-mono text-4xl font-bold tracking-[0.3em]">
                            {redeemCode}
                        </p>

                        <div className="flex items-center justify-center gap-2 text-green-200">
                            <Timer size={16} />
                            <span className="font-mono text-sm font-medium">
                                Expira en {formatTime(timeLeft)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Points Card */}
                <div className="flex flex-col items-center gap-3 rounded-3xl bg-indigo-50 p-6">
                    {brandInfo?.logoUrl ? (
                        <img
                            src={getLogoUrl(brandInfo.logoUrl)}
                            alt={brandInfo.name}
                            className="h-16 w-16 rounded-full border-2 border-white object-cover shadow-md"
                        />
                    ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-md">
                            <Store size={28} className="text-indigo-400" />
                        </div>
                    )}
                    <p className="text-sm font-medium text-gray-500">Puntos disponibles</p>
                    <p className="text-4xl font-bold text-indigo-600">
                        {balance.toLocaleString()}
                    </p>
                    <p className={`text-xs text-gray-400 mt-1 text-center ${brandInfo?.pointsExpirationMonths != null && brandInfo.pointsExpirationMonths > 0 ? 'block' : 'hidden'}`}>
                        {brandInfo?.pointsExpirationMonths != null && brandInfo.pointsExpirationMonths > 0
                            ? `Los puntos caducan a los ${brandInfo.pointsExpirationMonths} meses de su obtención.`
                            : '\u00A0'}
                    </p>

                    {/* QR Scanner / OTP Section */}
                    {brandInfo?.posMode === 'QR' ? (
                        <button
                            onClick={() => handleStartScan('points')}
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-4 font-bold text-white shadow-lg shadow-indigo-100 transition-all active:scale-[0.98]"
                        >
                            <Camera size={20} />
                            Escanear QR de Compra
                        </button>
                    ) : (
                        <div className="mt-4 w-full">
                            {!showLoyaltyCode ? (
                                <button
                                    onClick={handleGenerateLoyaltyCode}
                                    disabled={isGeneratingLoyalty}
                                    className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-2xl bg-indigo-600 py-4 text-white shadow-lg shadow-indigo-100 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {isGeneratingLoyalty ? (
                                        <Loader2 size={24} className="animate-spin" />
                                    ) : (
                                        <QrCode size={24} strokeWidth={1.8} />
                                    )}
                                    <span className="font-bold">
                                        {isGeneratingLoyalty ? 'Generando...' : 'Registrar puntos'}
                                    </span>
                                </button>
                            ) : (
                                <div className="relative rounded-2xl bg-indigo-600 p-5 text-white shadow-lg shadow-indigo-100">
                                    <button
                                        onClick={handleCancelLoyaltyCode}
                                        className="absolute right-3 top-3 rounded-full p-1 transition-colors hover:bg-white/20"
                                    >
                                        <X size={18} />
                                    </button>

                                    <p className="mb-1 text-center text-xs font-medium text-indigo-200">
                                        Tu código de cliente
                                    </p>

                                    <p className="mb-2 text-center font-mono text-3xl font-bold tracking-[0.3em]">
                                        {loyaltyOtp}
                                    </p>

                                    <div className="flex items-center justify-center gap-2 text-indigo-200">
                                        <Timer size={14} />
                                        <span className="font-mono text-xs font-medium">
                                            Expira en {formatTime(loyaltyTimeLeft)}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <p className="mt-3 text-center text-xs text-gray-400 italic">
                                Muestra tu código en caja para sumar puntos.
                            </p>
                        </div>
                    )}
                </div>

                {/* Saldo en € (StoredValue) Card — only for brands with the module enabled.
                    "saldo" alone means points elsewhere, so this is labelled explicitly in €. */}
                {brandInfo?.hasStoredValue && (
                    <div className="flex flex-col gap-2 rounded-3xl bg-emerald-50 p-6 border border-emerald-100">
                        <div className="flex items-center gap-2 text-emerald-700">
                            <Wallet size={18} />
                            <p className="text-sm font-medium">Saldo disponible (€)</p>
                        </div>
                        {purse ? (
                            <>
                                <p className="text-3xl font-bold text-emerald-700">
                                    {formatEuro(purse.totalBalance)}
                                </p>
                                {purse.bonusBalance > 0 && (
                                    <p className="text-xs text-emerald-600">
                                        Incluye {formatEuro(purse.bonusBalance)} de bono
                                    </p>
                                )}
                            </>
                        ) : purseError ? (
                            <p className="text-sm text-emerald-700/80">{purseError}</p>
                        ) : (
                            <div className="h-9 w-32 animate-pulse rounded-lg bg-emerald-100" />
                        )}

                        {/* Online recharge (Redsys redirect) — only when the Brand supports it
                            (StoredValue module + Redsys configured). Otherwise only the in-person
                            (scan-at-counter) recharge below is offered. */}
                        {brandInfo.supportsOnlineTopUp && (
                            <button
                                onClick={handleStartOnlineTopUp}
                                className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-100 transition-all active:scale-[0.98]"
                            >
                                <CreditCard size={18} />
                                Recargar online
                            </button>
                        )}

                        {/* Entry points for the two StoredValue scan flows. */}
                        <div className="mt-3 flex gap-3">
                            <button
                                onClick={() => handleStartScan('topup')}
                                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-white py-3 text-sm font-bold text-emerald-700 border border-emerald-200 shadow-sm transition-all active:scale-[0.98]"
                            >
                                <Plus size={18} />
                                {brandInfo.supportsOnlineTopUp ? 'Recargar en caja' : 'Recargar saldo'}
                            </button>
                            <button
                                onClick={() => handleStartScan('spend')}
                                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-100 transition-all active:scale-[0.98]"
                            >
                                <CreditCard size={18} />
                                Pagar con saldo
                            </button>
                        </div>
                    </div>
                )}

                {/* Operation error (recarga / pago) — inline & blocking (e.g. saldo insuficiente). */}
                {opError && (
                    <div className="flex items-start gap-3 rounded-3xl bg-red-50 p-5 border border-red-100">
                        <AlertTriangle className="shrink-0 text-red-500 mt-0.5" size={20} />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">{opError}</p>
                            <button
                                onClick={() => setOpError(null)}
                                className="mt-2 text-xs font-medium text-red-700 underline"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                )}

                {/* Top-up Success Banner (+€X) */}
                {topUpSuccess && (
                    <div className="flex flex-col items-center gap-2 rounded-3xl bg-emerald-50 p-6 border border-emerald-100 animate-in fade-in zoom-in">
                        <CheckCircle2 className="text-emerald-500" size={32} />
                        <p className="text-center font-bold text-emerald-800">
                            ¡Recarga completada!
                        </p>
                        <p className="text-2xl font-black text-emerald-600">
                            +{formatEuro(topUpSuccess.amount)}
                        </p>
                        <button
                            onClick={() => setTopUpSuccess(null)}
                            className="text-sm text-emerald-700 font-medium underline mt-1"
                        >
                            Cerrar
                        </button>
                    </div>
                )}

                {/* Spend Success Banner (-€X · +Z pts) */}
                {spendSuccess && (
                    <div className="flex flex-col items-center gap-2 rounded-3xl bg-emerald-50 p-6 border border-emerald-100 animate-in fade-in zoom-in">
                        <CheckCircle2 className="text-emerald-500" size={32} />
                        <p className="text-center font-bold text-emerald-800">
                            ¡Pago realizado con éxito!
                        </p>
                        <p className="text-2xl font-black text-emerald-600">
                            -{formatEuro(spendSuccess.amount)} · +{spendSuccess.pointsEarned} pts
                        </p>
                        <button
                            onClick={() => setSpendSuccess(null)}
                            className="text-sm text-emerald-700 font-medium underline mt-1"
                        >
                            Cerrar
                        </button>
                    </div>
                )}

                {/* Scan Success Banner */}
                <div className={`flex flex-col items-center gap-2 rounded-3xl bg-green-50 p-6 border border-green-100 animate-in fade-in zoom-in ${scanSuccess ? 'flex' : 'hidden'}`}>
                    <CheckCircle2 className="text-green-500" size={32} />
                    <p className="text-center font-bold text-green-800">
                        ¡Puntos sumados con éxito!
                    </p>
                    <p className="text-2xl font-black text-green-600">
                        +{scanSuccess?.pointsEarned ?? 0} pts
                    </p>
                    <button
                        onClick={() => setScanSuccess(null)}
                        className="text-sm text-green-700 font-medium underline mt-1"
                    >
                        Cerrar
                    </button>
                </div>

                {/* Invite a Friend Card */}
                <div className={`flex flex-col gap-3 rounded-3xl bg-orange-50 p-6 shadow-sm border border-orange-100 relative overflow-hidden ${brandInfo?.isReferralActive && userProfile?.personalReferralCode ? 'flex' : 'hidden'}`}>
                    <div className="flex items-start justify-between">
                        <div className="flex-1 pr-4">
                            <h3 className="text-lg font-bold text-orange-800 mb-1">
                                ¡Gana puntos invitando a tus amigos!
                            </h3>
                            <p className="text-sm text-orange-600/90 leading-relaxed mb-4">
                                Invita a un amigo a {brandInfo?.name} y ambos ganaréis puntos extra en su primera compra.
                            </p>
                        </div>
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-500">
                            <Gift size={24} />
                        </div>
                    </div>

                    <button
                        onClick={handleShare}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 py-3 font-semibold text-white shadow-sm transition-all hover:bg-orange-600 active:scale-[0.98]"
                    >
                        <Share2 size={18} />
                        <span>Compartir enlace de invitación</span>
                    </button>
                </div>

                {/* Rewards List */}
                <div>
                    <h2 className="mb-3 text-lg font-semibold text-gray-800">
                        Premios disponibles
                    </h2>

                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-20 animate-pulse rounded-2xl bg-gray-100" />
                            ))}
                        </div>
                    ) : rewards.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                            <Gift size={40} className="mx-auto mb-3 text-gray-300" />
                            <p className="text-sm text-gray-400">
                                Esta marca aún no tiene premios disponibles.
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {rewards.map((reward) => {
                                const canAfford = balance >= (reward.pointsCost ?? 0);
                                const isThisGenerating = isGenerating === reward.id;

                                return (
                                    <div
                                        key={reward.id}
                                        className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
                                    >
                                        {/* Icon */}
                                        <div
                                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${canAfford ? 'bg-indigo-50' : 'bg-gray-100'
                                                }`}
                                        >
                                            {canAfford ? (
                                                <Gift size={20} className="text-indigo-500" />
                                            ) : (
                                                <Lock size={20} className="text-gray-400" />
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-gray-900 truncate">
                                                {reward.name}
                                            </p>
                                            <p className="font-medium text-xs text-gray-900">
                                                {reward.description}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {(reward.pointsCost ?? 0).toLocaleString()} pts
                                            </p>
                                        </div>

                                        {/* Redeem Button */}
                                        <button
                                            onClick={() => handleRedeem(reward)}
                                            disabled={!canAfford || isThisGenerating}
                                            className={`shrink-0 cursor-pointer rounded-xl px-4 py-2 text-xs font-semibold transition-all active:scale-[0.96] disabled:cursor-not-allowed ${canAfford
                                                ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700'
                                                : 'bg-gray-100 text-gray-400'
                                                }`}
                                        >
                                            {isThisGenerating ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <span>Canjear</span>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Toast Notification */}
            <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 ${showToast ? 'block' : 'hidden'}`}>
                <div className="rounded-full bg-green-600/95 px-6 py-3 text-sm font-medium text-white shadow-lg backdrop-blur-sm">
                    <span>¡Enlace copiado al portapapeles!</span>
                </div>
            </div>

            {/* Pre-charge confirmation (Spend) — the User authorizes the amount here;
                only "Confirmar pago" actually debits the Purse (ADR-0007). */}
            {pendingSpend && (() => {
                // When the Purse is known and can't cover the amount, block up front
                // (mirrors the backend's "saldo insuficiente" — no partial payment).
                // If the Purse is unknown (still loading / failed), let the server decide.
                const insufficient = purse !== null && pendingSpend.amount > purse.totalBalance;
                return (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center">
                    <div className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl animate-in slide-in-from-bottom-4 sm:rounded-3xl">
                        <div className="mb-5 flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">Confirmar pago</h3>
                            <button
                                onClick={handleCancelSpend}
                                disabled={isConfirmingSpend}
                                className="rounded-full p-1 transition-colors hover:bg-gray-100 disabled:opacity-50"
                            >
                                <X size={22} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="flex flex-col items-center gap-1 rounded-2xl bg-emerald-50 p-5 border border-emerald-100">
                            <p className="text-sm font-medium text-emerald-700">Vas a pagar</p>
                            <p className="text-4xl font-black text-emerald-700">
                                {formatEuro(pendingSpend.amount)}
                            </p>
                        </div>

                        <div className="mt-4 flex flex-col gap-3">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Saldo restante</span>
                                <span className="font-bold text-gray-900">
                                    {formatEuro(Math.max((purse?.totalBalance ?? 0) - pendingSpend.amount, 0))}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Ganas</span>
                                <span className="font-bold text-indigo-600">
                                    +{previewPointsEarned(pendingSpend.amount)} pts
                                </span>
                            </div>
                        </div>

                        {insufficient && (
                            <div className="mt-4 flex items-start gap-2 rounded-2xl bg-red-50 p-4 border border-red-100">
                                <AlertTriangle className="shrink-0 text-red-500 mt-0.5" size={18} />
                                <p className="text-sm font-medium text-red-800">
                                    No tienes saldo suficiente. Recarga o usa otro medio de pago.
                                </p>
                            </div>
                        )}

                        <button
                            onClick={handleConfirmSpend}
                            disabled={isConfirmingSpend || insufficient}
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-4 font-bold text-white shadow-lg shadow-emerald-100 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isConfirmingSpend ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Procesando...
                                </>
                            ) : (
                                <>
                                    <CreditCard size={20} />
                                    Confirmar pago
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleCancelSpend}
                            disabled={isConfirmingSpend}
                            className="mt-2 w-full py-3 text-sm font-medium text-gray-500 disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
                );
            })()}

            {/* Online recharge sheet — pick an amount, preview the bonus, then get redirected
                to the Brand's Redsys hosted page. No card data is ever entered in the app. */}
            {showOnlineTopUp && (() => {
                const amount = onlineAmount ?? 0;
                const bonus = resolveTopUpBonus(amount, brandInfo?.topUpBonusTiers);
                const total = amount + bonus;
                return (
                    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center">
                        <div className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl animate-in slide-in-from-bottom-4 sm:rounded-3xl">
                            <div className="mb-5 flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900">Recargar saldo online</h3>
                                <button
                                    onClick={handleCloseOnlineTopUp}
                                    disabled={isCreatingPayment}
                                    className="rounded-full p-1 transition-colors hover:bg-gray-100 disabled:opacity-50"
                                >
                                    <X size={22} className="text-gray-500" />
                                </button>
                            </div>

                            <p className="mb-3 text-sm font-medium text-gray-500">Elige cuánto quieres recargar</p>
                            <div className="grid grid-cols-3 gap-2">
                                {topUpAmounts.map((value) => {
                                    const selected = onlineAmount === value;
                                    return (
                                        <button
                                            key={value}
                                            onClick={() => setOnlineAmount(value)}
                                            disabled={isCreatingPayment}
                                            className={`rounded-2xl py-3 text-sm font-bold transition-all active:scale-[0.97] disabled:opacity-60 ${selected
                                                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-100'
                                                : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                                }`}
                                        >
                                            {formatEuro(value)}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Bonus preview + total credited, mirroring the backend's tiers. */}
                            <div className="mt-5 flex flex-col gap-2 rounded-2xl bg-emerald-50 p-5 border border-emerald-100">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">Recarga</span>
                                    <span className="font-bold text-gray-900">{formatEuro(amount)}</span>
                                </div>
                                {bonus > 0 && (
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="flex items-center gap-1 text-emerald-700">
                                            <Sparkles size={14} />
                                            Bono
                                        </span>
                                        <span className="font-bold text-emerald-700">+{formatEuro(bonus)}</span>
                                    </div>
                                )}
                                <div className="mt-1 flex items-center justify-between border-t border-emerald-100 pt-2 text-sm">
                                    <span className="font-medium text-gray-700">Saldo que recibirás</span>
                                    <span className="text-lg font-black text-emerald-700">{formatEuro(total)}</span>
                                </div>
                            </div>

                            {onlineTopUpError && (
                                <div className="mt-4 flex items-start gap-2 rounded-2xl bg-red-50 p-4 border border-red-100">
                                    <AlertTriangle className="shrink-0 text-red-500 mt-0.5" size={18} />
                                    <p className="text-sm font-medium text-red-800">{onlineTopUpError}</p>
                                </div>
                            )}

                            <button
                                onClick={handleConfirmOnlineTopUp}
                                disabled={isCreatingPayment || amount <= 0}
                                className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-4 font-bold text-white shadow-lg shadow-emerald-100 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isCreatingPayment ? (
                                    <>
                                        <Loader2 size={20} className="animate-spin" />
                                        Redirigiendo…
                                    </>
                                ) : (
                                    <>
                                        <CreditCard size={20} />
                                        Continuar al pago
                                    </>
                                )}
                            </button>

                            <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400">
                                <ShieldCheck size={14} />
                                Pago seguro en la página de tu banco (Redsys). No guardamos tu tarjeta.
                            </p>
                        </div>
                    </div>
                );
            })()}

            {/* Scanner Modal */}
            {showScanner && (
                <div className="fixed inset-0 z-50 flex flex-col bg-black">
                    <div className="flex items-center justify-between p-6 text-white">
                        <h3 className="text-lg font-bold">
                            {cameraError
                                ? 'Cámara no disponible'
                                : scanMode === 'topup'
                                    ? 'Escanea el QR de recarga'
                                    : scanMode === 'spend'
                                        ? 'Escanea el QR de pago'
                                        : 'Escaneando QR...'}
                        </h3>
                        <button onClick={() => { setShowScanner(false); setCameraError(null); }} className="p-2 bg-white/10 rounded-full">
                            <X size={24} />
                        </button>
                    </div>

                    {cameraError === 'denied' ? (
                        <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
                            <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-white/10">
                                <CameraOff size={36} className="text-red-400" />
                            </div>

                            <h2 className="mb-2 text-xl font-bold text-white">
                                Acceso a la cámara denegado
                            </h2>
                            <p className="mb-6 max-w-xs text-sm leading-relaxed text-white/70">
                                Para poder escanear el código y sumar puntos, necesitamos acceso a tu cámara.
                            </p>

                            <div className="mb-8 w-full max-w-xs space-y-4 rounded-2xl bg-white/10 p-5 text-left text-sm text-white/80">
                                <div>
                                    <p className="mb-1 font-semibold text-white">📱 iPhone / Safari</p>
                                    <p>Toca el icono <strong>'aA'</strong> en la barra de direcciones y selecciona <strong>'Permitir cámara'</strong>.</p>
                                </div>
                                <div className="border-t border-white/10 pt-4">
                                    <p className="mb-1 font-semibold text-white">🤖 Android / Chrome</p>
                                    <p>Toca el <strong>candado</strong> en la barra de direcciones y ve a <strong>'Permisos'</strong>.</p>
                                </div>
                            </div>

                            <button
                                onClick={() => handleStartScan(scanMode)}
                                className="flex items-center gap-2 rounded-2xl bg-indigo-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-indigo-700 active:scale-[0.98]"
                            >
                                <RefreshCw size={16} />
                                Ya lo he permitido, reintentar
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex-1 flex items-center justify-center">
                                <div id="reader" className="w-full max-w-sm overflow-hidden rounded-2xl"></div>
                            </div>

                            <div className="flex flex-col items-center gap-6 p-8 text-center text-white/70 text-sm">
                                <p>Enfoca el código QR mostrado en la caja del negocio.</p>
                                
                                <button
                                    onClick={() => {
                                        Sentry.captureMessage("Reporte manual de usuario por bloqueo visual de cámara", "warning");
                                    }}
                                    className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-xs text-white transition-colors hover:bg-white/20"
                                    type="button"
                                >
                                    <AlertTriangle size={14} />
                                    ¿Problemas con la cámara?
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
